/**
 * The rest of the question "which repository?" — which branch of it, and which
 * folder inside it.
 *
 * These two used to live on the configure step, behind the App card's Edit.
 * That put them AFTER the detection: we read the default branch at the
 * repository root, filled the form from what we found there, and only then
 * offered to change the two inputs that decide what we should have read. For a
 * monorepo — the case where the folder matters at all — every value on the
 * step arrived wrong, and fixing them re-ran the whole thing.
 *
 * Asked here, they are answered before anything is read: the detection's first
 * attempt is at the right branch and the right folder. They stay editable on
 * the configure step, where the App card still holds both.
 *
 * The branch list is a convenience, never a gate. `BranchSelect` is a
 * combobox: a repository whose branches fail to load, or whose branch is past
 * the one-page cap, is still reachable by typing — and Continue is never
 * blocked on a listing that we could not fetch.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { GitHubRepository, RepositoryBuildInfo, RepositoryPathStatus } from '@back4app2/sdk';
import back4app2 from '../../back4app2';
import BranchSelect from '../BranchSelect/BranchSelect';
import RepositoryBrowser from '../RepositoryBrowser/RepositoryBrowser';
import TextInput from '../FormInputField/TextInput/TextInput';
import { DsButton } from '../ds';
import { AlertIcon, FolderIcon } from '../ds/icons';
import { ReactComponent as Spinner } from '../../assets/images/status-spinner.svg';
import { SUPPORTED_STACKS_SENTENCE } from '../BuildMethodSelector/stackLogos';
import { ROOT_DIRECTORY_TIP } from '../../utils/fieldHelp';

/**
 * Same wait the configure step uses before it reads a repository. Branch and
 * folder are free text: without it every keystroke is a GitHub listing.
 */
const DETECTION_DEBOUNCE_MS = 600;

const ConfirmRepository = ({ repository, onBack, onContinue }: {
  repository: GitHubRepository,
  /** Closes the drawer, leaving the list as it was. */
  onBack: () => void,
  onContinue: (branchName: string, rootDirectoryPath: string) => void,
}) => {
  const [branchName, setBranchName] = useState(repository.defaultBranch || '');
  const [rootDirectoryPath, setRootDirectoryPath] = useState('');
  const [branches, setBranches] = useState<string[]>([]);
  const [isLoadingBranches, setIsLoadingBranches] = useState(false);
  const [isBrowsing, setIsBrowsing] = useState(false);
  /**
   * What is in the folder about to be confirmed.
   *
   * The configure step asks this too, and finding nothing there is a bad
   * moment to find it: the person has left the list, the form has filled
   * itself with answers about a folder with no app in it, and getting back to
   * a different repository is two steps up. Here the branch and the folder are
   * both on screen, the browser is one press away, and the subfolders we found
   * an app in are pressable — so the answer is offered where the question is.
   *
   * Never a gate. The detection predicts what the builder will decide; a false
   * negative must not stop a deploy that would have worked, so Continue stays
   * exactly as available as it was.
   */
  const [buildInfo, setBuildInfo] = useState<RepositoryBuildInfo | null>(null);
  const [isChecking, setIsChecking] = useState(true);
  // Only the newest listing may write: switching repositories quickly would
  // otherwise let a slow first response land on top of the right one.
  const requestRef = useRef(0);
  /**
   * The first read starts at once; every later one waits.
   *
   * The debounce is there for typing, and nobody has typed when the drawer
   * opens — the values came from the repository. Six hundred milliseconds of
   * held breath before the request is even sent is the whole wait, twice, for
   * the one read that is holding Continue.
   */
  const isFirstDetectionRef = useRef(true);

  const loadBranches = useCallback(() => {
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;

    setIsLoadingBranches(true);

    back4app2
      .repositoryBranches(repository.id)
      .then(result => {
        if (requestRef.current === requestId) {
          setBranches(result);
        }
      })
      .catch(e => {
        // Not surfaced: the field still takes a typed branch, and an error on
        // a listing nobody asked for would read as the repository being
        // broken.
        console.error('error listing repository branches', e);

        if (requestRef.current === requestId) {
          setBranches([]);
        }
      })
      .finally(() => {
        if (requestRef.current === requestId) {
          setIsLoadingBranches(false);
        }
      });
  }, [repository.id]);

  useEffect(() => { loadBranches(); }, [loadBranches]);

  useEffect(() => {
    let cancelled = false;

    // Flipped HERE, before the debounce, not inside the request.
    //
    // It used to wait for the request to start, so that a folder being typed
    // did not blink the line once per keystroke. That was right while this
    // was only a warning. It is wrong now that Continue waits on it: between
    // the folder changing and the request starting, the answer on screen
    // belonged to the PREVIOUS folder, the notice was gone and the button was
    // live — so a browse into an empty folder left a second in which it could
    // be confirmed without ever having been read.
    //
    // A verdict is about one branch and one path. The moment either changes it
    // is about neither, and saying nothing is the only honest thing to say.
    setIsChecking(true);

    const timeout = setTimeout(async () => {
      if (cancelled) {
        return;
      }

      let result: RepositoryBuildInfo | null = null;

      try {
        result = await back4app2.repositoryBuildInfo(
          repository.id,
          branchName,
          rootDirectoryPath
        );
      } catch (e) {
        // Fail quiet, the way the configure step does. This is a warning about
        // a folder, not a report on whether we could reach GitHub — and an
        // error here would read as the repository being broken.
        console.error('error checking repository build info', e);
      }

      if (cancelled) {
        return;
      }

      setBuildInfo(result);
      setIsChecking(false);
    }, isFirstDetectionRef.current ? 0 : DETECTION_DEBOUNCE_MS);

    isFirstDetectionRef.current = false;

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [repository.id, branchName, rootDirectoryPath]);

  // Every read, not just the first. The button waits on this one, so a read
  // in flight has to look like a read in flight — including the one that
  // starts when a folder is browsed into.
  const isAnalyzing = isChecking;

  // Only about a real folder: a mistyped path is NOT_FOUND, and saying "no app
  // here" about a place that does not exist answers the wrong question.
  const nothingDetected =
    !isChecking &&
    !!buildInfo &&
    buildInfo.pathStatus === RepositoryPathStatus.DIRECTORY &&
    !buildInfo.hasDockerfile &&
    !buildInfo.detectedStack;

  const deployableDirectories = buildInfo?.deployableDirectories || [];

  return <>
    {/* Reads the branch that is selected right now: browsing before choosing
        a branch would list the default one's folders and hand back a path
        that may not exist on the branch being built. */}
    {isBrowsing && (
      <RepositoryBrowser
        open={isBrowsing}
        repositoryId={repository.id}
        branchName={branchName}
        initialPath={rootDirectoryPath}
        onClose={() => setIsBrowsing(false)}
        // It closes itself after selecting — `onSelect` only has to take the
        // path.
        onSelect={setRootDirectoryPath}
      />
    )}

    {/* A drawer under the row that opened it, not a screen of its own. The
        repository's name is on that row, so this repeats neither the name nor
        a way back to a list it never replaced — closing it is the same button
        that opened it. */}
    <div className="px-4 lg:px-5 pb-5 pt-4 flex flex-col gap-4 border-t border-[rgba(32,138,236,0.18)] bg-[rgba(32,138,236,0.055)] shadow-[inset_0_1px_0_rgba(255,255,255,.035)]">
      <div className="grid gap-4 lg:grid-cols-2">
        {/* No label of its own: BranchSelect draws one, and two of them
            stacked read as two fields. */}
        <div className="flex flex-col">
          <BranchSelect
            value={branchName}
            branches={branches}
            onChange={setBranchName}
            onRefresh={loadBranches}
            isLoading={isLoadingBranches}
            compact
          />
        </div>

        {/* Typed OR browsed, exactly as the configure step has always offered
            it. A path in a monorepo is three levels of names nobody recalls
            exactly, and this control is now the FIRST place it is asked for —
            leaving only the text box here would have moved the question
            earlier and taken away the thing that answers it.

            Browse is always offered, whatever the repository looks like: we
            only learn that a path has no subfolders after reading it, so a
            button that greyed itself out would do so a second late, and an
            empty listing is also what a mistyped path produces — exactly when
            the browser is the way out. */}
        <TextInput
          value={rootDirectoryPath}
          label="Root directory"
          name="rootDirPath"
          onChange={event => setRootDirectoryPath(event.target.value)}
          placeholderText="./"
          infoTip={ROOT_DIRECTORY_TIP}
          labelClassName="!text-ds-115"
          className="lg:!h-9 !bg-navy-900/20 !border-navy-500/60 hover:!border-navy-400/80 focus-within:!border-action/60 focus-within:!shadow-none"
          inputClassName="!text-ds-115 lg:!text-ds-115"
          hintClassName="!text-ds-105 hidden sm:block"
          endAdornment={
            <button
              type="button"
              onClick={() => setIsBrowsing(true)}
              className="b4-focus inline-flex items-center gap-1.5 whitespace-nowrap rounded-control px-2 py-1 !text-ds-105 text-navy-200 hover:text-white transition-colors duration-[120ms]"
            >
              <FolderIcon size={12} /> Browse folders
            </button>
          }
        />
      </div>

      {/* Between the fields and Continue, which is the order the decision is
          made in: what we found, then whether to go on. It shares the row with
          the buttons on a wide viewport so the drawer does not grow a third
          band — the fields, the answer and the way forward read as one block.

          No "Pick another folder" button here, unlike the configure step's
          notice: Browse folders is already in the field above it. */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        {isAnalyzing ? (
          <div className="flex items-center gap-2 min-w-0 text-ds-105 leading-[1.5] text-navy-200">
            <Spinner className="animate-spin flex-none" width="13px" height="13px" />
            Analyzing this repository…
          </div>
        ) : nothingDetected ? (
          <div className="flex items-start gap-2 min-w-0 text-ds-105 leading-[1.5] text-navy-200">
            <AlertIcon size={14} className="flex-none mt-[3px] text-state-err" />
            <div className="min-w-0">
              <span className="text-white">
                No app detected in {rootDirectoryPath.trim() || './'}
              </span>
              {/* The stacks, so the first question this raises — "is my
                  technology supported at all?" — is answered before anyone
                  leaves. Not on a phone: it is five lines there, in a drawer
                  whose job is two fields and a button. */}
              <span className="hidden sm:inline">
                {' '}· we build {SUPPORTED_STACKS_SENTENCE}
              </span>
              {deployableDirectories.length ? (
                <div className="mt-0.5">
                  Found in{' '}
                  {deployableDirectories.map((directory, index) => (
                    <span key={directory}>
                      {index > 0 ? <span aria-hidden="true"> · </span> : null}
                      {/* Sets the field above, which re-runs this check — so
                          pressing one is the same act as typing it, and the
                          line answers itself. */}
                      <button
                        type="button"
                        className="b4-focus font-courrier text-action-soft hover:underline"
                        onClick={() => setRootDirectoryPath(directory)}
                      >
                        {directory}
                      </button>
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        ) : <span />}

      <div className="flex flex-none justify-end gap-2">
        <DsButton
          variant="ghost"
          size="md"
          className="!h-7 !px-3 !text-ds-115"
          onClick={onBack}
        >
          Cancel
        </DsButton>
        {/* Never disabled on the branch being empty: a repository with no
            commits has no default branch to offer, and the configure step
            asks for one again before anything is created. */}
        <DsButton
          variant="action"
          size="md"
          className="!h-7 !px-3 !text-ds-115"
          onClick={() => onContinue(branchName.trim(), rootDirectoryPath.trim())}
          // Until the read lands, and then until it finds something.
          //
          // The second half is a choice made against the grain of the check:
          // `detectStack` predicts what the builder will decide and is a
          // SUBSET of what it actually supports, so a repository it does not
          // recognise can still build. Blocking here means such a repository
          // cannot be deployed from this screen at all — there is no way past
          // this button. It is blocked anyway, because the far more common
          // case is a monorepo pointed at its own root, and letting that
          // through spends a whole configure step, a create, and a failed
          // first build to say what this line already says.
          isDisabled={isAnalyzing || nothingDetected}
        >
          Continue
        </DsButton>
      </div>
      </div>
    </div>
  </>;
};

export default ConfirmRepository;
